import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminStore } from "@/zapi-admin/stores/admin-store";
import { updateCustomSnippetsUrl } from "@/zapi-admin/shared/services/updateCustomSnippetsUrl";
import { useAuthenticated } from "@/zapi-admin/shared/hooks/useAuthenticated";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@libs/zapi-ui/shadcn";
import { AppButton, AppInput, FigmaTypography } from "@libs/zapi-ui/custom";

export function UserSettingsPage() {
  const { user, setCustomSnippetsUrl } = useAdminStore();
  const { refetch } = useAuthenticated();
  const [url, setUrl] = useState(user?.customSnippetsUrl || "");
  const [isEditing, setIsEditing] = useState(false);

  const mutation = useMutation({
    mutationFn: updateCustomSnippetsUrl,
    onSuccess: (response) => {
      toast.success("Custom snippets URL updated successfully");
      setCustomSnippetsUrl(response.data.customSnippetsUrl);
      setIsEditing(false);
      refetch();
    },
    onError: () => {
      toast.error("Failed to update custom snippets URL");
      setUrl(user?.customSnippetsUrl || "");
    }
  });

  const handleSave = () => {
    const urlToSave = url.trim() === "" ? null : url.trim();
    mutation.mutate({ customSnippetsUrl: urlToSave });
  };

  const handleCancel = () => {
    setUrl(user?.customSnippetsUrl || "");
    setIsEditing(false);
  };

  const handleClear = () => {
    setUrl("");
    mutation.mutate({ customSnippetsUrl: null });
  };
  return (
    <div className="container mx-auto py-8 px-4" data-testid="user-settings-page">
      <div className="max-w-2xl mx-auto">
        <FigmaTypography component="h1" variant="heading-1" className="mb-8" data-testid="user-settings-heading">User Settings</FigmaTypography>

        <Card style={{ padding: "16px" }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">Custom Snippets URL</CardTitle>
            <CardDescription>
              Configure a custom URL for fetching AI prompts. When set, this URL will be used instead of the default
              snippets service for all admin operations.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {!isEditing ? (
              <div className="space-y-4">
                <div className="min-h-[40px] flex items-center">
                  {user?.customSnippetsUrl ? (
                    <div className="flex items-center gap-3">
                      <code className="text-[14px] bg-gray-100 px-3 py-2 rounded border">{user.customSnippetsUrl}</code>
                    </div>
                  ) : (
                    <p className="text-gray-500">No custom snippets URL configured. Using default service.</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <AppButton appearance="outlined" onClick={() => setIsEditing(true)} data-testid="user-settings-edit-button">
                    {user?.customSnippetsUrl ? "Edit" : "Configure"}
                  </AppButton>
                  {user?.customSnippetsUrl && (
                    <AppButton appearance="outlined" onClick={handleClear} disabled={mutation.isPending}>
                      Clear
                    </AppButton>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label htmlFor="snippets-url" className="block text-[14px] font-medium text-gray-700 mb-2">
                    Snippets URL
                  </label>
                  <AppInput
                    id="snippets-url"
                    type="url"
                    placeholder="https://your-snippets-service.com"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    disabled={mutation.isPending}
                    data-testid="user-settings-snippets-input"
                  />
                </div>

                <div className="flex gap-2">
                  <AppButton appearance="outlined" onClick={handleSave} disabled={mutation.isPending} data-testid="user-settings-save-button">
                    {mutation.isPending ? "Saving..." : "Save"}
                  </AppButton>
                  <AppButton appearance="outlined" onClick={handleCancel} disabled={mutation.isPending} data-testid="user-settings-cancel-button">
                    Cancel
                  </AppButton>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
